import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '@sureco/design-system'
import {
  Alert,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Typography,
  message,
} from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  downloadFile,
  getTerminationFileByPublicKey,
  TerminationFileDetailDto,
} from '../api/terminations-enrollment-platform-api-client'
import { TerminationFileRowDto } from '../api/dto/termination-file-row.dto'

dayjs.extend(utc)

const { Text } = Typography
const { Search } = Input

const PAGE_SIZE = 10

const rowMatchesSearch = (row: TerminationFileRowDto, search: string) => {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [row.employeeId, row.firstName, row.lastName].some((value) =>
    String(value ?? '')
      .toLowerCase()
      .includes(needle)
  )
}

type Props = {
  open: boolean
  fileKeys: string[]
  highlightEmployeeId?: string | null
  onClose: () => void
}

const formatTermDate = (value?: Date | string) => {
  if (!value) return '—'
  const date = dayjs.utc(value)
  if (!date.isValid()) {
    return String(value)
  }
  return date.format('MM/DD/YYYY')
}

const TerminationSourceFileModal = ({
  open,
  fileKeys,
  highlightEmployeeId,
  onClose,
}: Props) => {
  const [selectedFileKey, setSelectedFileKey] = useState<string>()
  const [file, setFile] = useState<TerminationFileDetailDto | null>(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!open) {
      setSelectedFileKey(undefined)
      setFile(null)
      setPage(1)
      setSearch('')
      setErrorMessage('')
      return
    }
    setSelectedFileKey(fileKeys[0])
    setPage(1)
    setSearch('')
  }, [open, fileKeys])

  useEffect(() => {
    if (!open || !selectedFileKey) {
      return
    }

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setErrorMessage('')
      try {
        const data = await getTerminationFileByPublicKey(selectedFileKey)
        if (!cancelled) {
          setFile(data)
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setFile(null)
          setErrorMessage('Failed to load source file rows.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [open, selectedFileKey])

  const filteredRows = useMemo(() => {
    const rows = file?.rows ?? []
    return rows.filter((row) => rowMatchesSearch(row, search))
  }, [file?.rows, search])

  useEffect(() => {
    if (!highlightEmployeeId) {
      setPage(1)
      return
    }
    const index = filteredRows.findIndex(
      (row) => row.employeeId === highlightEmployeeId
    )
    setPage(index >= 0 ? Math.floor(index / PAGE_SIZE) + 1 : 1)
  }, [filteredRows, highlightEmployeeId])

  const columns = useMemo(
    () => [
      { title: 'Emp ID', dataIndex: 'employeeId', key: 'employeeId' },
      { title: 'First name', dataIndex: 'firstName', key: 'firstName' },
      { title: 'Last name', dataIndex: 'lastName', key: 'lastName' },
      {
        title: 'Term date',
        dataIndex: 'terminationDate',
        key: 'terminationDate',
        render: (value: Date | string | undefined) => formatTermDate(value),
      },
      { title: 'Status', dataIndex: 'status', key: 'status' },
    ],
    []
  )

  const handleDownload = async () => {
    if (!selectedFileKey) return
    setDownloading(true)
    try {
      await downloadFile(
        selectedFileKey,
        file?.fileName || 'termination_file.csv'
      )
    } catch (err) {
      console.error(err)
      message.error('Failed to download source file')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Modal
      title={file?.fileName ? `Source: ${file.fileName}` : 'Source file'}
      open={open}
      onCancel={onClose}
      width={900}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="darkblue"
            onClick={handleDownload}
            loading={downloading}
            disabled={!selectedFileKey || loading}
          >
            Download
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {fileKeys.length > 1 ? (
          <Select
            value={selectedFileKey}
            onChange={setSelectedFileKey}
            options={fileKeys.map((key, index) => ({
              value: key,
              label:
                key === selectedFileKey && file?.fileName
                  ? file.fileName
                  : `Source file ${index + 1}`,
            }))}
            style={{ width: 320 }}
          />
        ) : null}
        <Search
          allowClear
          placeholder="Search by emp ID or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={setSearch}
        />
        <Text type="secondary">
          {file?.rowCount != null
            ? search.trim()
              ? `${filteredRows.length} of ${file.rowCount} rows match.`
              : `${file.rowCount} rows in this source file.`
            : 'Showing all rows in this source file.'}
        </Text>
        {errorMessage ? (
          <Alert type="error" showIcon message={errorMessage} />
        ) : null}
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 240,
            }}
          >
            <Spin />
          </div>
        ) : (
          <Table
            rowKey={(_, index) => String(index)}
            dataSource={filteredRows}
            columns={columns}
            size="small"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              hideOnSinglePage: true,
              onChange: setPage,
            }}
            scroll={{ y: 420 }}
            rowClassName={(row: TerminationFileRowDto) =>
              highlightEmployeeId && row.employeeId === highlightEmployeeId
                ? 'ant-table-row-selected'
                : ''
            }
            locale={{ emptyText: 'No source rows available' }}
          />
        )}
      </div>
    </Modal>
  )
}

export default TerminationSourceFileModal
